import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Users,
  ShieldOff,
  Flag,
  LogOut,
  Trash2,
  AlertTriangle,
  User as UserIcon,
  Sun,
  Moon,
  Smartphone,
  Bookmark,
  UserPen,
  FileText,
  BookOpen,
  ScrollText,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";

/**
 * Settings root — 1:1 port of iOS `mobile/src/app/(app)/settings/index.tsx`.
 *
 * Sections (in order): PROFILE · SAFETY · LEGAL · APPEARANCE · ACCOUNT ·
 * Danger zone card · Version bar footer.  Every button/row's testID
 * matches the iOS source verbatim so shared automation tools work on both
 * platforms.
 */

const BRAND = "#7638FA";
const DANGER = "#FF3B30";

const PRIVACY_URL = "https://brontoy.com/privacy";
const GUIDELINES_URL = "https://brontoy.com/guidelines";
const TERMS_URL = "https://brontoy.com/terms";

/* -----------------------------------------------------------------------
 * Section wrapper — bold uppercase eyebrow above a rounded card.
 * ----------------------------------------------------------------------- */
function Section({ label, children }) {
  return (
    <div className="mt-6" data-testid={`settings-section-${label.toLowerCase()}`}>
      <p
        className="text-muted-foreground uppercase font-bold ml-1 mb-2.5"
        style={{ fontSize: 12, letterSpacing: 1 }}
      >
        {label}
      </p>
      <div className="rounded-[20px] border border-border bg-card px-3">
        {children}
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Link row — icon tile + title + subtitle + chevron. Used across sections.
 * ----------------------------------------------------------------------- */
function LinkRow({ Icon, title, subtitle, iconColor, onClick, testId, danger, last }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 py-3 px-1 text-left hover:bg-secondary/50 active:opacity-70 transition-all ${
        last ? "" : "border-b border-border"
      }`}
      data-testid={testId}
    >
      <span
        className="w-[38px] h-[38px] rounded-[12px] flex items-center justify-center shrink-0"
        style={{
          backgroundColor: `${iconColor || BRAND}18`,
        }}
      >
        <Icon
          size={18}
          strokeWidth={2}
          style={{ color: iconColor || BRAND }}
        />
      </span>
      <div className="flex-1 min-w-0">
        <div className={`text-[15px] font-semibold ${danger ? "text-destructive" : "text-foreground"}`}>
          {title}
        </div>
        {subtitle && (
          <div className="text-muted-foreground text-[12.5px] mt-0.5 truncate">
            {subtitle}
          </div>
        )}
      </div>
      <ChevronRight size={17} className="text-muted-foreground shrink-0" />
    </button>
  );
}

/* -----------------------------------------------------------------------
 * Appearance segmented control — System / Light / Dark, active = card bg.
 * ----------------------------------------------------------------------- */
function AppearanceSegment({ mode, onChange }) {
  const opts = [
    { key: "system", label: "System", Icon: Smartphone, testId: "appearance-system" },
    { key: "light", label: "Light", Icon: Sun, testId: "appearance-light" },
    { key: "dark", label: "Dark", Icon: Moon, testId: "appearance-dark" },
  ];
  return (
    <div className="flex bg-secondary rounded-[14px] p-1 gap-1">
      {opts.map(({ key, label, Icon, testId }) => {
        const active = mode === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className={`flex-1 h-[42px] rounded-[10px] flex items-center justify-center gap-1.5 transition-all ${
              active ? "" : "opacity-70 hover:opacity-90"
            }`}
            style={active ? { backgroundColor: "hsl(var(--card))" } : {}}
            data-testid={testId}
          >
            <Icon
              size={17}
              strokeWidth={1.9}
              className={active ? "text-foreground" : "text-muted-foreground"}
            />
            <span
              className={`text-[13.5px] ${
                active ? "text-foreground font-bold" : "text-muted-foreground font-semibold"
              }`}
            >
              {label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Modal wrapper — dim overlay + centred card + click-outside close.
 * ----------------------------------------------------------------------- */
function Modal({ open, onClose, blockClose, children, testId }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-7 bg-black/45"
      onClick={blockClose ? undefined : onClose}
      data-testid={testId}
    >
      <div
        className="w-full max-w-[360px] rounded-[24px] bg-card p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
export default function Settings() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { mode, setMode } = useTheme();

  const [logoutOpen, setLogoutOpen] = useState(false);
  const [logoutBusy, setLogoutBusy] = useState(false);
  // 0 = closed, 1 = first confirm, 2 = "are you absolutely sure?"
  const [deleteStep, setDeleteStep] = useState(0);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const back = () =>
    window.history.length > 1 ? navigate(-1) : navigate("/home", { replace: true });

  const doLogout = async () => {
    setLogoutBusy(true);
    try {
      await logout();
      toast.success("Signed out");
      setLogoutOpen(false);
      navigate("/", { replace: true });
    } catch {
      toast.error("Couldn't log out. Try again.");
    } finally {
      setLogoutBusy(false);
    }
  };

  const doDelete = async () => {
    if (deleteBusy) return;
    if (deleteStep === 1) {
      setDeleteStep(2);
      return;
    }
    setDeleteBusy(true);
    try {
      await api.post(EP.deleteAccount, {
        user_id: Number(user?.id),
        username: user?.username,
      });
      toast.success("Account deleted — We're sorry to see you go.");
      await logout();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(`Couldn't delete account — ${apiError(e)}`);
    } finally {
      setDeleteBusy(false);
      setDeleteStep(0);
    }
  };

  const external = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="min-h-[100dvh] w-full bg-background text-foreground flex flex-col"
      data-testid="settings-screen"
    >
      {/* Header */}
      <div
        className="h-[52px] px-2 flex items-center border-b border-border shrink-0"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <button
          type="button"
          onClick={back}
          className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-secondary transition-colors"
          aria-label="Back"
          data-testid="settings-back"
        >
          <ChevronLeft size={25} strokeWidth={2.4} />
        </button>
        <h1
          className="flex-1 text-center text-foreground font-jakarta font-bold text-[17px] pr-10"
          style={{ letterSpacing: "-0.3px" }}
        >
          Settings
        </h1>
      </div>

      {/* Scroll body */}
      <div className="flex-1 overflow-y-auto px-4 pb-14">
        <div className="mx-auto md:mx-0 w-full max-w-[560px] md:max-w-none">
          {/* PROFILE */}
          <Section label="Profile">
            <LinkRow
              Icon={UserPen}
              title="Edit Profile"
              subtitle="Photo, name, bio, privacy and more."
              iconColor={BRAND}
              onClick={() => navigate("/settings/edit-profile")}
              testId="settings-edit-profile-link"
              last
            />
          </Section>

          {/* SAFETY */}
          <Section label="Safety">
            <LinkRow
              Icon={Bookmark}
              title="Saved posts"
              subtitle="View all the posts you've saved."
              onClick={() => navigate("/saved")}
              testId="settings-saved-posts-link"
            />
            <LinkRow
              Icon={Users}
              title="Follow requests"
              subtitle="Approve or reject people who want to follow you."
              onClick={() => navigate("/settings/follow-requests")}
              testId="settings-follow-requests-link"
            />
            <LinkRow
              Icon={ShieldOff}
              title="Blocked accounts"
              subtitle="Manage who can't interact with you."
              onClick={() => navigate("/settings/blocked")}
              testId="settings-blocked-link"
            />
            <LinkRow
              Icon={Flag}
              title="My reports"
              subtitle="Reports you've submitted and their status."
              onClick={() => navigate("/settings/reports")}
              testId="settings-reports-link"
              last
            />
          </Section>

          {/* LEGAL */}
          <Section label="Legal">
            <LinkRow
              Icon={FileText}
              title="Privacy Policy"
              onClick={() => external(PRIVACY_URL)}
              testId="settings-legal-privacy"
            />
            <LinkRow
              Icon={BookOpen}
              title="Community Guidelines"
              onClick={() => external(GUIDELINES_URL)}
              testId="settings-legal-guidelines"
            />
            <LinkRow
              Icon={ScrollText}
              title="Terms of Use"
              onClick={() => external(TERMS_URL)}
              testId="settings-legal-terms"
              last
            />
          </Section>

          {/* APPEARANCE */}
          <div className="mt-6">
            <p
              className="text-muted-foreground uppercase font-bold ml-1 mb-2.5"
              style={{ fontSize: 12, letterSpacing: 1 }}
            >
              Appearance
            </p>
            <div className="rounded-[20px] border border-border bg-card p-3">
              <AppearanceSegment mode={mode} onChange={setMode} />
            </div>
          </div>

          {/* ACCOUNT */}
          <div className="mt-6">
            <p
              className="text-muted-foreground uppercase font-bold ml-1 mb-2.5"
              style={{ fontSize: 12, letterSpacing: 1 }}
            >
              Account
            </p>
            <div className="rounded-[20px] border border-border bg-card p-4 flex flex-col gap-3">
              {/* Signed-in-as row */}
              <div className="flex items-center gap-2 px-1">
                <UserIcon size={16} className="text-muted-foreground" />
                <span className="text-muted-foreground text-[13.5px]">
                  Signed in as{" "}
                  <span className="text-foreground font-semibold">
                    @{user?.username || "you"}
                  </span>
                </span>
              </div>

              <button
                type="button"
                onClick={() => setLogoutOpen(true)}
                className="w-full h-[50px] rounded-[16px] flex items-center justify-center gap-2 transition-colors"
                style={{
                  borderWidth: 1,
                  borderColor: DANGER,
                  backgroundColor: "transparent",
                }}
                data-testid="settings-logout"
              >
                <LogOut size={18} style={{ color: DANGER }} />
                <span
                  className="font-bold"
                  style={{ color: DANGER, fontSize: 15 }}
                >
                  Log out
                </span>
              </button>
            </div>
          </div>

          {/* DANGER ZONE — red gradient card */}
          <button
            type="button"
            onClick={() => setDeleteStep(1)}
            className="w-full mt-6 rounded-[20px] flex items-center gap-3.5 px-4 py-4 active:scale-[0.99] active:opacity-92 transition-all"
            style={{
              background: "linear-gradient(135deg, #FF6B6B 0%, #FF3B30 50%, #D91E36 100%)",
              boxShadow: "0 6px 20px -6px rgba(255, 59, 48, 0.5)",
            }}
            data-testid="settings-delete-account"
          >
            <span
              className="w-[42px] h-[42px] rounded-full flex items-center justify-center shrink-0"
              style={{ backgroundColor: "rgba(255,255,255,0.22)" }}
            >
              <Trash2 size={20} className="text-white" strokeWidth={2.2} />
            </span>
            <div className="flex-1 text-left">
              <div
                className="text-white font-extrabold"
                style={{ fontSize: 16, letterSpacing: "-0.2px" }}
              >
                Delete Account
              </div>
              <div
                className="mt-0.5"
                style={{ fontSize: 12.5, color: "rgba(255,255,255,0.82)" }}
              >
                Permanently delete your account and data.
              </div>
            </div>
            <ChevronRight size={20} style={{ color: "rgba(255,255,255,0.9)" }} />
          </button>
        </div>
      </div>

      {/* Logout modal */}
      <Modal
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        blockClose={logoutBusy}
      >
        <h3 className="text-foreground text-[17px] font-bold text-center">
          Log out of BrontoY?
        </h3>
        <p className="text-muted-foreground text-[14px] text-center mt-1.5">
          You&apos;ll need to sign in again to continue.
        </p>
        <button
          type="button"
          onClick={doLogout}
          disabled={logoutBusy}
          className="w-full h-[52px] rounded-[16px] mt-5 flex items-center justify-center text-white font-bold disabled:opacity-70"
          style={{ backgroundColor: DANGER, fontSize: 16 }}
          data-testid="settings-logout-confirm"
        >
          {logoutBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Log out"}
        </button>
        <button
          type="button"
          onClick={() => setLogoutOpen(false)}
          disabled={logoutBusy}
          className="w-full h-[50px] mt-1.5 text-foreground font-semibold hover:opacity-70 disabled:opacity-50"
          style={{ fontSize: 16 }}
        >
          Cancel
        </button>
      </Modal>

      {/* Delete-account modal (2-step) */}
      <Modal
        open={deleteStep > 0}
        onClose={() => (deleteBusy ? null : setDeleteStep(0))}
        blockClose={deleteBusy}
      >
        <div
          className="w-[54px] h-[54px] rounded-full flex items-center justify-center mx-auto mb-3.5"
          style={{ backgroundColor: `${DANGER}1A` }}
        >
          <AlertTriangle size={26} style={{ color: DANGER }} strokeWidth={2.2} />
        </div>
        <h3 className="text-foreground text-[17px] font-bold text-center">
          {deleteStep === 1 ? "Delete Account?" : "Are you absolutely sure?"}
        </h3>
        <p className="text-muted-foreground text-[14px] text-center mt-1.5 leading-5">
          {deleteStep === 1
            ? "This action cannot be undone and will permanently delete this account and all of its data."
            : `This will permanently erase @${user?.username} — posts, followers, messages, everything. There is no way back.`}
        </p>
        <button
          type="button"
          onClick={doDelete}
          disabled={deleteBusy}
          className="w-full h-[52px] rounded-[16px] mt-4 flex items-center justify-center text-white font-bold disabled:opacity-70"
          style={{ backgroundColor: DANGER, fontSize: 16 }}
          data-testid={
            deleteStep === 1 ? "delete-account-confirm" : "delete-account-confirm-final"
          }
        >
          {deleteBusy ? (
            <Loader2 className="w-5 h-5 animate-spin" data-testid="delete-account-loading" />
          ) : deleteStep === 1 ? (
            "Delete"
          ) : (
            "Yes, delete my account"
          )}
        </button>
        <button
          type="button"
          onClick={() => (deleteBusy ? null : setDeleteStep(0))}
          disabled={deleteBusy}
          className="w-full h-[50px] mt-1.5 text-foreground font-semibold hover:opacity-70 disabled:opacity-50"
          style={{ fontSize: 16 }}
          data-testid="delete-account-cancel"
        >
          Cancel
        </button>
      </Modal>
    </div>
  );
}
